import React, { useEffect, useRef, useState, useCallback } from "react";
import "./payment-details.css";
import MetaTags from "react-meta-tags";
import {
    Button,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    useMediaQuery,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { ROLES } from "../../utils/constants";
import DefaultPUWidget from "../../Widgets/PU/Default";
import DefaultWidget from "../../Widgets/SU/Default";
import { connect, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import PageHeader from "../../common/PageHeader";
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { toTitleCase } from "../../utils/helper";
import { setAlert, setCreditCardForm } from "../../../redux/actions";
import AlertConfirmModal from "../../common/AlertConfirmModal";

const PaymentDetails = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const isMobile = useMediaQuery("(max-width: 899px)");

    const [isGettingInfo, setIsGettingInfo] = useState(false);
    const [defaultAddress, setDefaultAddress] = useState({});
    const [defaultPayment, setDefaultPayment] = useState(null);
    const [openOptions, setOpenOptions] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const isMountedRef = useRef(true);
    const cancelTokenSource = useRef();
    const cancelToken = axios.CancelToken;
    const source = cancelToken.source();

    cancelTokenSource.current = source;

    const onSuccess = () => {
        getCardInformation();
    };

    useEffect(() => {
        getCardInformation();

        const handleSuccess = () => {
            onSuccess();
        };

        window.addEventListener("credit-card-form/success", handleSuccess);

        return () => {
            window.removeEventListener(
                "credit-card-form/success",
                handleSuccess
            );
            isMountedRef.current = false;
            cancelRequest();
        };
    }, []);

    const cancelRequest = useCallback(() => {
        if (cancelTokenSource.current) {
            cancelTokenSource.current.cancel("Abort request");
        }
    }, []);

    const getCardInformation = async () => {
        setIsGettingInfo(true);
        await axios
            .get("/api/cards")
            .then((response) => {
                if (!isMountedRef.current) return null;
                if (response.data && Object.keys(response.data).length > 0) {
                    setDefaultPayment(response.data);
                    setDefaultAddress({
                        address: {
                            city: response.data.billing_details.address.city,
                            country:
                                response.data.billing_details.address.country,
                            line1: response.data.billing_details.address.line1,
                            line2: response.data.billing_details.address.line2,
                            postal_code:
                                response.data.billing_details.address
                                    .postal_code,
                            state: response.data.billing_details.address
                                .postal_code,
                        },
                        name: response.data.billing_details.name,
                    });
                } else {
                    setDefaultPayment(null);
                    setDefaultAddress({});
                }

                setIsGettingInfo(false);
            })
            .catch((error) => {
                console.log({ error });
                setIsGettingInfo(false);
            });
    };

    const addCard = () => {
        dispatch(setCreditCardForm({ show: true, mode: "add", card: {} }));
    };

    const handleOptions = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseOptions = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        dispatch(
            setCreditCardForm({
                show: true,
                mode: "edit",
                card: defaultPayment,
            })
        );
        setAnchorEl(null);
    };

    const handleRemove = () => {
        setShowConfirm(true);
        setAnchorEl(null);
    };

    const onRemoveConfirm = () => {
        setIsDeleting(true);
        axios
            .delete("/api/cards")
            .then((response) => {
                dispatch(
                    setAlert({
                        show: true,
                        type: "success",
                        body: "Card successfully removed",
                        onClose: () => getCardInformation(),
                    })
                );
                setIsDeleting(false);
            })
            .catch((error) => {
                console.error(error);

                dispatch(
                    setAlert({
                        show: true,
                        type: "Error",
                        body: "Unable to remove card",
                    })
                );

                setIsDeleting(false);
            });
    };

    useEffect(() => {
        setOpenOptions(Boolean(anchorEl));
    }, [anchorEl]);

    useEffect(() => {
        return () => {
            dispatch(setCreditCardForm({ mode: "add" }));
        };
    }, []);

    const onBack = () => {
        history.push("/home");
    };

    return (
        <div className="container">
            <MetaTags>
                <title>
                    Payment Method &#8211; Be Inspired, be seduced and be
                    surprised by the splendor of our Goddesses.
                </title>
                <meta
                    name="description"
                    content={`${config.appName} is a place you can connect, and be entertained, seduced, inspired and surprised with our beautiful Goddesses`}
                />
            </MetaTags>
            <div className="payment-card-details-container">
                <PageHeader
                    title="Payment Method"
                    backable={true}
                    onBack={onBack}
                    closable={false}
                />

                <div
                    css={css`
                        margin: 12px auto;
                        text-align: center;
                    `}
                >
                    <img src={"/images/cards.png"} border="0" width={150} />
                </div>

                {!isGettingInfo ? (
                    <div
                        css={css`
                            margin: 15px 0;
                            display: flex;
                            justify-content: flex-end;
                            gap: 12px;
                            padding: 0 12px;
                        `}
                    >
                        {defaultPayment &&
                        Object.keys(defaultPayment).length > 0 ? (
                            <React.Fragment>
                                {!isDeleting ? (
                                    <Button
                                        variant="contained"
                                        onClick={handleEdit}
                                        sx={{
                                            borderRadius: 20,
                                            fontWeight: "bold",
                                        }}
                                        size="small"
                                    >
                                        Edit
                                    </Button>
                                ) : null}
                                <Button
                                    variant="contained"
                                    onClick={handleRemove}
                                    sx={{
                                        borderRadius: 20,
                                        fontWeight: "bold",
                                    }}
                                    size="small"
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? "Removing..." : "Remove"}
                                </Button>
                            </React.Fragment>
                        ) : null}

                        {(!defaultPayment ||
                            Object.keys(defaultPayment).length === 0) &&
                        !isGettingInfo ? (
                            <Button
                                variant="contained"
                                onClick={addCard}
                                sx={{ borderRadius: 20, fontWeight: "bold" }}
                                size="small"
                            >
                                Add Card
                            </Button>
                        ) : null}
                    </div>
                ) : null}
                <div
                    css={css`
                        margin: 12px auto;
                        font-size: 12px;
                        padding: 0 12px;
                    `}
                >
                    <p>
                        Please enter your payment card and address details as
                        these are secured with 3D secure, and sent to your bank
                        for approval. We only store the last 4 digits of your
                        credit card and expiry date in your secured account for
                        your convenience and compliance purposes.
                    </p>

                    <p>We accept valid Mastercard, Visa and Amex cards.</p>
                </div>

                {isGettingInfo ? (
                    <div
                        style={{
                            display: "flex",
                            flex: 1,
                            justifyContent: "center",
                            padding: "0 12px",
                        }}
                    >
                        <CircularProgress size={24} />
                    </div>
                ) : (
                    <div
                        css={css`
                            padding: 0 12px;
                        `}
                    >
                        <div>
                            {!defaultPayment ||
                            Object.keys(defaultPayment).length == 0 ? (
                                <div>No payment card found</div>
                            ) : (
                                <div
                                    css={css`
                                        display: flex;
                                        gap: 20px;
                                        border: 1px solid #ccc;
                                        border-radius: 24px;
                                        padding: 8px 14px;
                                        align-items: center;
                                    `}
                                >
                                    <div css={css``}>
                                        {toTitleCase(
                                            defaultPayment.cards.brand
                                        )}
                                    </div>

                                    <div css={css``}>
                                        •••• {defaultPayment.cards.last4}
                                    </div>

                                    <div css={css``}>
                                        {defaultPayment.billing_details.name}
                                    </div>

                                    <div css={css``}>
                                        Expiry:{" "}
                                        {defaultPayment.cards.exp_month > 9
                                            ? defaultPayment.cards.exp_month
                                            : "0" +
                                              defaultPayment.cards.exp_month}
                                        /{defaultPayment.cards.exp_year}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <AlertConfirmModal
                modalBody={`This will remove your payment card details from the secure merchant bank and all future purchases will require a payment card to be added.`}
                show={showConfirm}
                setShow={setShowConfirm}
                isConfirm={true}
                useFunction={onRemoveConfirm}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.user.user,
});

export default connect(mapStateToProps)(PaymentDetails);
